/* eslint-disable @typescript-eslint/naming-convention */
import { z } from 'zod/v4';

import { tryJsonParse } from '@blockworks/platform';

import { ExecutionDataSchema, type ExecutionMetadata } from './schemas';

// ===== Core Types =====

export type ValidationStage = 'schema' | 'data';

export interface ValidationResult<T> {
    success: boolean;
    data?: T;
    errors?: ValidationError[];
    warnings?: ValidationWarning[];
}

export interface ValidationError {
    stage: ValidationStage;
    field?: string;
    message: string;
    code?: string;
}

export interface ValidationWarning {
    stage: ValidationStage;
    field?: string;
    message: string;
}

// Minimal args needed for schema creation
export interface MinimalArgs<TColumns extends string = string> {
    execution: {
        metadata: ExecutionMetadata<TColumns>;
    };
}

// ===== Simplified Builder Pattern =====

/**
 * Final stage of the builder with workflow - ready to build the processor
 */
class ProcessorWithWorkflow<TArgs, TResponse, TColumns extends string> {
    constructor(
        private readonly schemaFactory: (metadata: ExecutionMetadata<TColumns>) => z.ZodSchema<TArgs>,
        private readonly workflowFn: (validatedArgs: TArgs) => TResponse | Promise<TResponse>,
    ) {}

    /**
     * Builds the final processor function that handles validation and execution
     */
    build(): (args: unknown) => Promise<ValidationResult<TResponse>> {
        return async (args: unknown): Promise<ValidationResult<TResponse>> => {
            const warnings: ValidationWarning[] = [];

            try {
                // Step 1: Parse minimal args to get metadata
                const minimalArgsSchema = z.object({
                    execution: ExecutionDataSchema,
                });

                const minimalArgsResult = minimalArgsSchema.safeParse(args);
                if (!minimalArgsResult.success) {
                    return {
                        success: false,
                        errors: [
                            {
                                stage: 'schema',
                                message: 'Invalid or missing execution metadata',
                                field: 'execution.metadata',
                                code: 'METADATA_MISSING',
                            },
                        ],
                    };
                }

                const metadata = minimalArgsResult.data.execution.metadata as ExecutionMetadata<TColumns>;

                // Step 2: Create and apply the dynamic schema
                const schema = this.schemaFactory(metadata);

                const validationResult = schema.safeParse(args);

                if (!validationResult.success) {
                    // Enhanced error formatting with available columns hint
                    const formattedErrors = validationResult.error.issues.map(err => {
                        const field = err.path.join('.');

                        return {
                            stage: 'schema' as ValidationStage,
                            field,
                            message: err.message,
                        };
                    });

                    return { success: false, errors: formattedErrors };
                }

                // Step 3: Execute the workflow with validated data
                const result = await this.workflowFn(validationResult.data);

                return {
                    success: true,
                    data: result,
                    warnings: warnings.length > 0 ? warnings : undefined,
                };
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown workflow error';
                let errors = [];

                const richErrors = tryJsonParse(errorMessage);
                if (richErrors.isJsonString) {
                    errors = richErrors.parsedJson.map((err: any) => {
                        const field = err.path.join('.');

                        return {
                            stage: 'schema' as ValidationStage,
                            field,
                            message: err.message,
                            code: err.code,
                        };
                    });
                } else {
                    errors = [
                        {
                            stage: 'data',
                            message: errorMessage,
                            code: 'WORKFLOW_ERROR',
                        },
                    ];
                }

                return {
                    success: false,
                    errors: errors,
                };
            }
        };
    }
}

/**
 * Builder stage with schema - can add workflow
 */
class ProcessorWithSchema<TArgs, TColumns extends string> {
    constructor(private readonly schemaFactory: (metadata: ExecutionMetadata<TColumns>) => z.ZodSchema<TArgs>) {}

    /**
     * Add the workflow function
     */
    workflow<TResponse>(
        workflowFn: (validatedArgs: TArgs) => TResponse | Promise<TResponse>,
    ): ProcessorWithWorkflow<TArgs, TResponse, TColumns> {
        return new ProcessorWithWorkflow(this.schemaFactory, workflowFn);
    }
}

/**
 * Initial processor builder
 */
class ProcessorBuilder {
    /**
     * Set the validation schema
     */
    schema<TArgs, TColumns extends string = string>(
        schemaFactory: (metadata: ExecutionMetadata<TColumns>) => z.ZodSchema<TArgs>,
    ): ProcessorWithSchema<TArgs, TColumns> {
        return new ProcessorWithSchema(schemaFactory);
    }
}

/**
 * Create a new visualization processor with chained builder pattern
 *
 * @example
 * const processor = createProcessor()
 *   .schema((metadata) => z.object({ ... }))
 *   .workflow(async (args) => { ... })
 *   .build();
 *
 * const result = await processor(args);
 */
export const createProcessor = () => new ProcessorBuilder();

/**
 * Extract the response type from a processor function
 */
export type ProcessorResponse<T> = T extends (args: unknown) => Promise<ValidationResult<infer R>> ? R : never;

/**
 * Extract the args type from a processor function
 */
export type ProcessorArgs<T> = T extends ProcessorWithWorkflow<infer A, any, any> ? A : never;
